import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import LockedPaneCard from '@/components/lock/LockedPaneCard';
import PrayerSidebar from './sidebar/PrayerSidebar';
import type { PrayerSidebarGroup } from './sidebar/PrayerGroupListItem';
import PrayerWallPage from './PrayerWallPage';
import CombinedPrayersPage from './CombinedPrayersPage';

const LAST_VIEW_KEY = 'flock_prayer_last_view';

function loadLastView(): string | null {
  try {
    return localStorage.getItem(LAST_VIEW_KEY);
  } catch {
    return null;
  }
}

function saveLastView(view: string) {
  try {
    localStorage.setItem(LAST_VIEW_KEY, view);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Prayer workspace: cross-org sidebar + content pane (ChatLayout pattern).
 * Routes: /prayers (auto-select last view), /prayers/all, /prayers/lifted,
 * /prayers/:groupId.
 */
export default function PrayersLayout() {
  const { groupId: viewParam } = useParams();
  const navigate = useNavigate();
  const {
    organizations,
    currentOrganization,
    currentGroup,
    switchOrganization,
    isSwitchingOrg,
    setCurrentGroup,
    isLoading,
  } = useAuth();
  const { isOrgLocked } = useOrgLockState();

  const isCombined = viewParam === 'all' || viewParam === 'lifted';

  // Resolve the selected group across ALL orgs (not just the current one)
  const selectedGroup = useMemo((): PrayerSidebarGroup | null => {
    if (!viewParam || isCombined) return null;
    for (const membership of organizations) {
      const group = membership.groups?.find((g) => g.id === viewParam);
      if (group) {
        return {
          id: group.id,
          name: group.name,
          orgId: membership.organization_id,
          orgName: membership.organization.name,
          imageUrl: group.group_image_url,
        };
      }
    }
    return null;
  }, [viewParam, isCombined, organizations]);

  const orgsLoaded = !isLoading && organizations.length > 0;

  // Auto-select on bare /prayers: last-used view → current group → All Prayers
  useEffect(() => {
    if (viewParam || !orgsLoaded) return;
    const last = loadLastView();
    if (last === 'all' || last === 'lifted') {
      navigate(`/prayers/${last}`, { replace: true });
      return;
    }
    const lastGroupExists =
      last && organizations.some((o) => o.groups?.some((g) => g.id === last));
    if (lastGroupExists) {
      navigate(`/prayers/${last}`, { replace: true });
      return;
    }
    navigate(currentGroup ? `/prayers/${currentGroup.id}` : '/prayers/all', { replace: true });
  }, [viewParam, orgsLoaded, organizations, currentGroup, navigate]);

  // Heal unknown group ids (left the group / bad deep link) once orgs load
  useEffect(() => {
    if (viewParam && !isCombined && orgsLoaded && !selectedGroup) {
      navigate('/prayers', { replace: true });
    }
  }, [viewParam, isCombined, orgsLoaded, selectedGroup, navigate]);

  // Persist the active view for next visit
  useEffect(() => {
    if (isCombined) saveLastView(viewParam!);
    else if (selectedGroup) saveLastView(selectedGroup.id);
  }, [viewParam, isCombined, selectedGroup]);

  // Deep links / refreshes can land on a cross-org group without going
  // through handleSelectGroup — rotate the org context so the wall can
  // mount. If the org context later moves away from a URL we were ALREADY
  // aligned with (the user switched orgs via the main sidebar switcher),
  // FOLLOW the user to the new org's group instead of switching back.
  //
  // alignedGroupRef is only written in the aligned state — never inside the
  // heal branch — so StrictMode's double-invoked effects can't flip the
  // heal into a follow. Heal attempts are capped to avoid a retry loop if
  // the org switch keeps failing (e.g. membership revoked server-side).
  const alignedGroupRef = useRef<string | null>(null);
  const healAttemptsRef = useRef<Record<string, number>>({});
  useEffect(() => {
    if (!selectedGroup || !currentOrganization || isSwitchingOrg) return;

    if (selectedGroup.orgId === currentOrganization.id) {
      alignedGroupRef.current = selectedGroup.id;
      healAttemptsRef.current = {};
      return;
    }

    if (alignedGroupRef.current === selectedGroup.id) {
      // User moved the org context out from under the URL — follow them.
      navigate(currentGroup ? `/prayers/${currentGroup.id}` : '/prayers/all', { replace: true });
      return;
    }

    // Deep link / refresh onto a cross-org group: heal by rotating the org.
    const attempts = healAttemptsRef.current[selectedGroup.id] ?? 0;
    if (attempts >= 2) {
      navigate('/prayers/all', { replace: true });
      return;
    }
    healAttemptsRef.current[selectedGroup.id] = attempts + 1;
    switchOrganization(selectedGroup.orgId, selectedGroup.id).catch((e) =>
      console.warn('[Prayers] Org switch for deep link failed:', e)
    );
  }, [
    selectedGroup,
    currentOrganization,
    isSwitchingOrg,
    switchOrganization,
    currentGroup,
    navigate,
  ]);

  const handleSelectGroup = useCallback(
    async (group: PrayerSidebarGroup) => {
      navigate(`/prayers/${group.id}`);

      // Cross-org group: rotate org context (claims) so REST writes work.
      if (group.orgId !== currentOrganization?.id) {
        try {
          await switchOrganization(group.orgId, group.id);
        } catch (e) {
          console.warn('[Prayers] Org switch on group select failed:', e);
        }
      } else {
        // Keep the app-level current group in sync with the prayer wall
        const membership = organizations.find((o) => o.organization_id === group.orgId);
        const fullGroup = membership?.groups.find((g) => g.id === group.id);
        if (fullGroup && fullGroup.id !== currentGroup?.id) setCurrentGroup(fullGroup);
      }
    },
    [
      navigate,
      currentOrganization?.id,
      switchOrganization,
      organizations,
      setCurrentGroup,
      currentGroup?.id,
    ]
  );

  return (
    <div className="flex h-full">
      <PrayerSidebar
        selectedKey={viewParam ?? null}
        onSelectAll={() => navigate('/prayers/all')}
        onSelectLifted={() => navigate('/prayers/lifted')}
        onSelectGroup={handleSelectGroup}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        {isCombined ? (
          <CombinedPrayersPage mode={viewParam as 'all' | 'lifted'} />
        ) : selectedGroup ? (
          isOrgLocked(selectedGroup.orgId) ? (
            // Paused org: placeholder instead of firing doomed prayer fetches.
            <LockedPaneCard organizationId={selectedGroup.orgId} />
          ) : currentOrganization?.id === selectedGroup.orgId ? (
            <PrayerWallPage key={selectedGroup.id} group={selectedGroup} />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
              Switching organization…
            </div>
          )
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
            {orgsLoaded ? 'Select a group to see its prayer wall.' : 'Loading…'}
          </div>
        )}
      </div>
    </div>
  );
}
